import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";

/**
 * Manages spine animation for Logo components
 * Handles initialization, scaling, and animation playback
 */
class LogoController {
  constructor(container, scaledWidth, scaledHeight, deviceConfig) {
    this.container = container;
    this.scaledWidth = scaledWidth;
    this.scaledHeight = scaledHeight;
    this.deviceConfig = deviceConfig;
    this.spine = null;
    this.isInitialized = false;
    this.desiredVisibility = false; // Store desired visibility state


    // Defer spine creation to avoid blocking the main thread
    requestAnimationFrame(() => {
      this.initializeSpineAnimation();
    });
  }

  initializeSpineAnimation() {
    this.spine = this.createSpine();
    if (!this.spine) {
      return;
    }

    if (
      !this.spine.skeleton ||
      !this.spine.state ||
      !this.spine.skeleton?.data
    ) {
      return;
    }

    this.container.addChild(this.spine);
    this.spine.position.set(0, 0);
    this.spine.zIndex = 5;
    this.spine.visible = true;
    this.spine.alpha = 0.001;
    this.spine.skeleton.setToSetupPose();

    if (this.spine.parent !== this.container) {
      return;
    }

    // Capture native dimensions ONCE at scale 1.0 before any scaling.
    // Many logo spines don't export width/height in skeleton data — without
    // caching, getLocalBounds() would measure already-scaled bounds on every
    // resize, creating a feedback loop where deviceMultiplier has no effect.
    this._captureNativeDimensions();

    this.applySpineScaling();
    // Play logo_idle (logo spine) or idle, fallback to first available
    const animToPlay =
      this.spine.skeleton.data.animations.find((a) => a.name === "logo_idle") ||
      this.spine.skeleton.data.animations.find((a) => a.name === "idle") ||
      this.spine.skeleton.data.animations[0];
    if (animToPlay) {
      this.playAnimation(animToPlay.name, true);
    }
    this.isInitialized = true;
    this.spine.alpha = this.desiredVisibility ? 1 : 0.001;
  }

  /**
   * Capture the spine's native (unscaled) dimensions and cache them.
   * Called once during initialization when the spine is at scale 1.0.
   *
   * Priority:
   * 1. Skeleton data width/height (set in Spine Editor export settings)
   * 2. getLocalBounds() measured at scale 1.0 (stable reference)
   *
   * Without this, spines that don't export width/height would have their
   * bounds re-measured after scaling, making deviceMultiplier inert.
   */
  _captureNativeDimensions() {
    this._cachedNativeWidth = 0;
    this._cachedNativeHeight = 0;

    if (!this.spine) return;

    // 1. Try skeleton data dimensions (set in Spine Editor export settings)
    const skeletonData = this.spine.skeleton?.data;
    if (skeletonData?.width && skeletonData?.height) {
      this._cachedNativeWidth = skeletonData.width;
      this._cachedNativeHeight = skeletonData.height;
      return;
    }

    // 2. Scan skin attachments for the largest bounding box
    //    This is synchronous, physics-safe, and works even before first render.
    //    We scan the default skin's region attachments for max width/height.
    try {
      const defaultSkin = this.spine.skeleton?.data?.defaultSkin;
      if (defaultSkin) {
        let maxW = 0;
        let maxH = 0;
        // Spine-pixi stores attachments in defaultSkin.attachments (Map-like)
        const attachments = defaultSkin.attachments;
        for (let i = 0; i < attachments.length; i++) {
          const slotAttachments = attachments[i];
          if (!slotAttachments) continue;
          for (const name in slotAttachments) {
            const att = slotAttachments[name];
            if (att && att.width && att.height) {
              if (att.width > maxW) maxW = att.width;
              if (att.height > maxH) maxH = att.height;
            }
          }
        }
        if (maxW > 0 && maxH > 0) {
          this._cachedNativeWidth = maxW;
          this._cachedNativeHeight = maxH;
          return;
        }
      }
    } catch {
      // Silent fail
    }

    // 3. Last resort: getLocalBounds (may return 0 before first render)
    try {
      this.spine.scale.set(1, 1);
      const bounds = this.spine.getLocalBounds();

      if (bounds && bounds.width > 0 && bounds.height > 0) {
        this._cachedNativeWidth = bounds.width;
        this._cachedNativeHeight = bounds.height;
      }
    } catch {
      // Silent fail
    }


  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "logo-skeleton";
      const atlasAlias = "logo-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  playAnimation(animationName, loop = false) {
    if (!this.spine || !this.spine.state) {
      return;
    }

    const animations = this.spine.skeleton.data.animations;
    const animation = animations.find((anim) => anim.name === animationName);

    if (!animation) {
      return;
    }

    this.spine.state.setAnimation(0, animationName, loop);
  }

  getNativeDimensions() {
    if (!this.spine?.skeleton?.data) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }

    const skeletonData = this.spine.skeleton.data;
    return {
      width: skeletonData.width || 0,
      height: skeletonData.height || 0,
      x: skeletonData.x || 0,
      y: skeletonData.y || 0,
    };
  }

  applySpineScaling() {
    if (!this.spine || !this.scaledWidth || !this.scaledHeight) {
      return;
    }

    // Same guard as IntroPopupController - spine must be in display tree
    if (!this.spine.scale || !this.spine.parent) {
      return;
    }

    if (!this.spine.skeleton || !this.spine.state) {
      return;
    }

    // Use cached native dimensions (captured once at scale 1.0)
    const width = this._cachedNativeWidth;
    const height = this._cachedNativeHeight;



    if (width && height) {
      const scaleX = this.scaledWidth / width;
      const scaleY = this.scaledHeight / height;
      const scale = Math.min(scaleX, scaleY);



      try {
        this.spine.scale.set(scale, scale);
        this.spine.position.set(0, 0);
      } catch {
        // Silent fail like IntroPopupController
      }
    } else {

    }
  }

  updateDimensions(newScaledWidth, newScaledHeight, newDeviceConfig) {
    const dimensionsChanged =
      this.scaledWidth !== newScaledWidth || this.scaledHeight !== newScaledHeight;



    this.scaledWidth = newScaledWidth;
    this.scaledHeight = newScaledHeight;
    if (newDeviceConfig) {
      this.deviceConfig = newDeviceConfig;
    }

    if (this.spine && this.isInitialized && dimensionsChanged) {
      this.applySpineScaling();
    }
  }

  setVisible(visible) {
    this.desiredVisibility = visible;
    if (this.spine && this.isInitialized) {
      this.spine.alpha = visible ? 1 : 0.001;
    }
  }

  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) {
      return [];
    }
    return this.spine.skeleton.data.animations.map((anim) => anim.name);
  }

  cleanup() {
    if (!this.spine) return;

    try {
      if (this.spine.state) {
        try {
          this.spine.state.clearListeners();
          this.spine.state.clearTracks();
        } catch {
          // Silent fail - state may already be destroyed
        }
      }
      if (this.container?.children?.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
    } catch {
      // Silent fail - spine/container may already be destroyed
    } finally {
      this.spine = null;
      this.isInitialized = false;
    }
  }
}

export default LogoController;
